<template>
	<li
		:id="'notification-' + notification.id"
		data-id="notification-card-item"
		class="nav notification-card"
		:class="'notification-card--' + notificationType.class"
	>
		<span class="notification-card__type">
			<i :class="'icon icon-' + notificationType.icon"></i>
		</span>
		<span v-if="!notification.shown" class="notification-card__close">
			<button
				class="notification-card__close-btn"
				data-id="notification-close-button"
				@click="closeAction"
			>
				<i class="icon icon-remove"></i>
			</button>
		</span>
		<div
			class="notification-card__content"
			:class="[
				'notification-card__content',
				hasLongWord && 'include-long-word'
			]"
			v-html="parsedHtml"
			data-id="notification-content"
		/>
		<div class="notification-card__date">
			<span
				class="notification-card__created-date"
				data-id="notification-created-date"
			>
				Created on: {{ formatDateTime(notification.datetimeStamp) }}
			</span>
			<span
				class="notification-card__dismissed-date"
				data-id="notification-dismissed-date"
				v-if="notification.dismissed && notification.dismissedOn"
			>
				Dismissed on: {{ formatDateTime(notification.dismissedOn) }}
			</span>
		</div>
		<div v-if="!notification.dismissed" class="notification-card__actions">
			<a
				class="notification-card__save-btn"
				data-id="notification-save-button"
				@click="saveAction"
			>
				Save &amp; Dismiss
			</a>
			<a
				class="notification-card__dismiss-btn"
				data-id="notification-dismiss-button"
				@click="dismissAction"
				v-log="{
					target: 'Dismiss Notification Link',
					event: 'click',
					eventData: notification
				}"
			>
				Dismiss
			</a>
		</div>
	</li>
</template>

<script>
import { hasWordOfLength } from '@/utils/longest-word';
import { dateTimeString } from '@/utils/date-functions';
import { mapActions, mapGetters } from 'vuex';
import getCompanyId from '@/composables/get-companyid';
import getQueryParameter from '@/utils/get-parameter-querystring';
import loggingMixin from '@/mixins/logging-mixin';

export default {
	name: 'VueNotificationCard',
	mixins: [loggingMixin],
	props: {
		notification: {
			type: Object,
			default: undefined
		}
	},
	data() {
		return {
			alertLinks: undefined,
			accessWebUrl: this.VUE_APP_ACCESSWEB_URL,
			dateElementsToFormat: undefined
		};
	},
	mounted() {
		this.setAlertlinkClickHandler();
	},
	methods: {
		...mapActions('user', [
			'showNotificationModalAction',
			'alertsShownAction'
		]),
		...mapActions('fileDownload', ['downloadFileAction']),
		setOpenLinkInNewWindow(e) {
			const labelElement = e.currentTarget;
			let alertLink = labelElement.getAttribute('data-alertLink');
			if (alertLink) {
				if (alertLink.includes('Parts/BackCounter'))
					this.showBackCounter(e);
				else {
					if (
						!alertLink.startsWith('http') &&
						!alertLink.startsWith('/')
					) {
						alertLink = `/${alertLink}`;
					}
					this.$emit('notificationClick');
					this.dismissAction();

					window.open(alertLink, '_blank');
				}
			}
		},
		showBackCounter(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			let roId = labelElement.getAttribute('data-roid');
			let cid = labelElement.getAttribute('data-cid');
			const source = 'BackCounter';
			roId = roId || getQueryParameter(link.link, 'roId');
			cid = cid || getQueryParameter(link.link, 'cid');
			const model = {
				roid: roId,
				cid: cid,
				source: source
			};

			this.showModal(model, link);
		},
		showMultiPoint(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const roId = labelElement.getAttribute('data-roid');
			const cid = labelElement.getAttribute('data-cid');

			const model = {
				roId: roId,
				cid: cid
			};

			this.showModal(model, link);
		},
		grabPartsRequest(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const roId = labelElement.getAttribute('data-roid');
			const cid = labelElement.getAttribute('data-cid');

			const model = {
				roId: roId,
				cid: cid
			};

			this.showModal(model, link);
		},
		showSideBySideProposal(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const orgCid = labelElement.getAttribute('data-cid');
			const parentScenarioId = labelElement.getAttribute(
				'data-scenarioid'
			);
			const fromEmployeeId = labelElement.getAttribute(
				'data-fromemployeeid'
			);

			const model = {
				orgCid: orgCid,
				parentScenarioId: parentScenarioId,
				fromEmployeeId: fromEmployeeId
			};

			this.showModal(model, link);
		},
		showTeamChat(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const itemType = labelElement.getAttribute('data-itemtype');
			const itemId = labelElement.getAttribute('data-itemid');
			const itemCID = labelElement.getAttribute('data-itemcid');

			const model = {
				itemType: itemType,
				itemId: itemId,
				itemCID: itemCID
			};

			this.showModal(model, link);
		},
		showManagerApproval(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const itemType = labelElement.getAttribute('data-itemdecision');
			const itemId = labelElement.getAttribute('data-itemid');

			const model = {
				itemId: itemId,
				itemType: itemType
			};

			this.showModal(model, link);
		},
		showCustomerMessage(e) {
			const labelElement = e.currentTarget;
			const link = this.linkProperties(labelElement);
			const itemType = labelElement.getAttribute('data-itemtype');
			const itemId = labelElement.getAttribute('data-itemid');
			const roNotifyReqsMet = labelElement.getAttribute(
				'data-roNotifyReqsMet'
			);
			const model = {
				itemType: itemType,
				itemId: itemId,
				RONotifyReqsMet: roNotifyReqsMet
			};

			this.showModal(model, link);
		},
		showModal(model, link) {
			this.$emit('notificationClick');
			this.dismissAction();

			this.showNotificationModalAction({
				alertLinkType: link.type,
				args: model
			});
		},
		saveAction() {
			const notice = { ...this.notification, ...{ save: true } };
			const params = {
				notification: notice,
				companyId: getCompanyId(this.$route)
			};
			this.$emit('saveClick', params);
		},
		dismissAction() {
			const notice = { ...this.notification, ...{ save: false } };
			const params = {
				notification: notice,
				companyId: getCompanyId(this.$route)
			};
			this.$emit('dismissClick', params);
		},
		closeAction() {
			const params = {
				notificationItems: [this.notification],
				companyId: getCompanyId(this.$route)
			};
			this.$emit('closeClick', params);
			this.alertsShownAction(params);
		},
		linkProperties(element) {
			const linkType = element.getAttribute('data-alertlinktype');
			const alertLink = element.getAttribute('data-alertlink');
			return { type: linkType, link: alertLink };
		},
		formatDateTime(date) {
			return dateTimeString(date);
		},
		async downloadFile(e) {
			const labelElement = e.currentTarget;
			const alertApiPath = labelElement.getAttribute('data-api-path');
			const alertApiParameters = JSON.parse(
				labelElement.getAttribute('data-api-parameters')
			);

			await this.downloadFileAction({
				apiPath: alertApiPath,
				params: alertApiParameters,
				reportName: alertApiParameters.reportName
			});

			this.$emit('notificationClick');
			this.dismissAction();
		},
		setAlertlinkClickHandler() {
			let clickHandler;

			//Will return an empty array if none found....
			this.alertLinks = this.$el.querySelectorAll('[data-alertlinktype]');

			for (let element of this.alertLinks) {
				const link = this.linkProperties(element);

				switch (link.type) {
					case 'BL':
						clickHandler = this.setOpenLinkInNewWindow;
						break;
					case 'LK':
						clickHandler = this.setOpenLinkInNewWindow;
						break;
					case 'CM':
						clickHandler = this.showCustomerMessage;
						break;
					case 'MG':
						clickHandler = this.showManagerApproval;
						break;
					case 'MP':
						clickHandler = this.showMultiPoint;
						break;
					case 'PRG':
						clickHandler = this.grabPartsRequest;
						break;
					case 'SP':
						clickHandler = this.showSideBySideProposal;
						break;
					case 'TC':
						clickHandler = this.showTeamChat;
						break;
					case 'DL':
						clickHandler = this.downloadFile;
						break;
					default:
						clickHandler = () => {};
						break;
				}

				element.onclick = clickHandler;
			}
		}
	},
	computed: {
		...mapGetters('user', ['vueAppSitePath', 'VUE_APP_ACCESSWEB_URL']),
		parsedHtml() {
			let html = this.notification.alertHTML;

			if (html) {
				html = html.replace('*sitepath*', this.vueAppSitePath);
			}

			return html;
		},
		hasLongWord() {
			const text = this.notification.alertHTML;
			return hasWordOfLength(text, 38);
		},
		formatCreatedDate() {
			return this.formatDateTime(this.notification.datetimeStamp);
		},
		formatDismissedOn() {
			return this.formatDateTime(this.notification.dismissedOn);
		},
		notificationType() {
			switch (this.notification.alertType) {
				case 'I':
					return { class: 'info', icon: 'info' };
				case 'W':
					return { class: 'warning', icon: 'warning' };
				case 'S':
					return { class: 'success', icon: 'complete' };
				case 'E':
					return { class: 'error', icon: 'warning-01' };
				case 'T':
					return { class: 'time', icon: 'start_time' };
				default:
					return { class: 'mail', icon: 'mail' };
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * [Description]
    *
    * Notification Card Item
    * Can use SCSS variables = TRUE
*/

.notification-card {
	display: grid;
	min-width: 200px;
	grid-template-columns: auto 6fr 16px;
	gap: $gutter-md;
	padding: $padding-sm;
	color: $ui-fg-light;
	border-radius: $border-radius;
	font-size: 12px;

	&--info {
		background: $ui-neutral;
	}

	&--warning {
		background: $ui-warning;
	}

	&--success {
		background: $ui-positive;
	}

	&--error {
		background: $ui-negative;
	}

	&--mail {
		background: $ui-indigo;
	}

	&--time {
		background: $neutral-4;
	}

	&__type {
		grid-column: 1/2;
		grid-row: 1/3;
		justify-self: center;
		padding-top: 2px;
		font-size: 18px;
	}

	&__close {
		grid-column: 3/3;
		grid-row: 1/3;
		font-size: 11pt;
		margin-top: -2px;
	}

	&__close-btn {
		margin-top: $gutter-xs;
		@include btn-clear;
		min-width: 16px;

		@include mq($from: md-l) {
			min-width: 16px;
		}

		> .icon {
			font-size: 12px;
		}
	}

	&__content {
		grid-column: 2/3;
		grid-row: 1/2;

		> *:first-child {
			display: block;
		}

		> h4,
		> h5 {
			font-size: 14px;
			margin: 0 0 $gutter-md 0;
			letter-spacing: 0.5px;
		}

		.jqx-link,
		.vue-link,
		.anchorLink,
		a {
			margin-right: $gutter-sm;
			line-height: 18px;
			cursor: pointer;
			border-bottom: 1px solid $ui-fg-light;

			&:hover {
				border-bottom: 1px solid transparent;
			}

			@include reset-focus-styles;
		}

		.anchorLink {
			display: inline-block;
		}

		label {
			display: inline;

			&.block + .block {
				margin-left: $gutter-sm;
			}

			&.white-bold-jqx-link {
				display: inline-block;
				margin: $gutter-md $gutter-sm 0 0;
				line-height: 18px;
				font-weight: $bold-font-weight;
				text-align: center;
				border-bottom: 1px solid $ui-fg-light;
				cursor: pointer;

				&:hover {
					border-bottom: 1px solid transparent;
				}

				@include reset-focus-styles;
			}
		}

		&.include-long-word {
			word-break: keep-all;
		}
	}

	&__date {
		grid-column: 2/3;
		grid-row: 2/3;
		font-size: 11px;
		margin-top: $gutter-sm;
		display: flex;
		flex-direction: column;
		gap: $gutter-sm;
	}

	&__actions {
		grid-column: 2/3;
		grid-row: 3/4;
		display: flex;
		justify-content: space-between;
		gap: $gutter-xl;
		margin-top: $gutter-lg;

		> * {
			border-bottom: 1px solid $ui-fg-light;
			cursor: pointer;

			&:hover,
			&:focus {
				border-color: transparent;
			}
		}
	}
}
</style>
